import { useMemo, useState } from "react";
import { Button, Flex, Input, Select, Space, Table, Typography, Modal, Form, message, Tooltip, Tag } from "antd";
import { ReloadOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import { gql, useQuery, useMutation } from "@apollo/client";

const GET_ALL_DRIVERS = gql`
  query GetAllDrivers(
    $email: String
    $idNumber: String
    $idType: String
    $phoneNumber: String
    $registrationStatus: DRIVER_REGISTRATION_STATUS
    $pagination: PaginationInput
  ) {
    getAllDrivers(
      filter: {
        email: $email
        idNumber: $idNumber
        idType: $idType
        phoneNumber: $phoneNumber
        registrationStatus: $registrationStatus
      }
      pagination: $pagination
    ) {
      hasMore
      limit
      offset
      total
      drivers {
        id
        name
        phoneNumber
        driverInfo {
          email
          idNumber
          idType
          registrationStatus
          rejectionReason
        }
      }
    }
  }
`;

const IDENTITY_TYPES_LOOKUP = gql`
  query IdentityTypesLookup {
    identityTypesLookup {
      errorCodes
      status
      data {
        id
        nameAr
        nameEn
      }
    }
  }
`;

const UPDATE_DRIVER_STATUS = gql`
  mutation UpdateDriverStatusByUserId($input: UpdateDriverStatusDto!) {
    updateDriverStatusByUserId(input: $input) {
      createdAt
      email
      id
      idNumber
      idType
      isVehicleOwner
      registrationStatus
      rejectionReason
      updatedAt
    }
  }
`;

type DriverInfo = {
  email?: string | null;
  idNumber?: string | null;
  idType?: "CITIZEN" | "RESIDENT" | null;
  registrationStatus?:
    | "PENDING"
    | "UNDER_REVIEW"
    | "APPROVED"
    | "REJECTED"
    | null;
  rejectionReason?: string | null;
} | null;

type IdentityType = {
  id: string;
  nameAr: string;
  nameEn: string;
};

type IdentityTypesLookupResponse = {
  identityTypesLookup?: {
    errorCodes?: (string | null)[] | null;
    status?: boolean | null;
    data?: IdentityType[] | null;
  } | null;
};

type Driver = {
  id?: number | null;
  name?: string | null;
  phoneNumber?: string | null;
  driverInfo?: DriverInfo;
} | null;

type DriverRegistrationStatus = NonNullable<
  NonNullable<DriverInfo>["registrationStatus"]
>;

const REGISTRATION_STATUS_OPTIONS: DriverRegistrationStatus[] = [
  "PENDING",
  "UNDER_REVIEW",
  "APPROVED",
  "REJECTED",
];

type GetAllDriversResponse = {
  getAllDrivers?: {
    hasMore?: boolean | null;
    limit?: number | null;
    offset?: number | null;
    total?: number | null;
    drivers?: Driver[] | null;
  } | null;
};

function DriverList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // Input state (what user types)
  const [emailInput, setEmailInput] = useState("");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [idNumberInput, setIdNumberInput] = useState("");
  const [idTypeInput, setIdTypeInput] = useState<string | undefined>();
  const [registrationStatusInput, setRegistrationStatusInput] = useState<DriverRegistrationStatus | undefined>();

  // Filter state (what is actually applied to query)
  const [emailFilter, setEmailFilter] = useState("");
  const [phoneNumberFilter, setPhoneNumberFilter] = useState("");
  const [idNumberFilter, setIdNumberFilter] = useState("");
  const [idTypeFilter, setIdTypeFilter] = useState<string | undefined>();
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState<DriverRegistrationStatus | undefined>();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<{ id: number; name: string } | null>(null);

  const { data: identityTypesData } = useQuery<IdentityTypesLookupResponse>(
    IDENTITY_TYPES_LOOKUP
  );

  const identityTypes: IdentityType[] =
    identityTypesData?.identityTypesLookup?.data ?? [];

  const identityTypesMap = useMemo(
    () =>
      identityTypes.reduce<Record<string, IdentityType>>((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {}),
    [identityTypes]
  );

  const { data, loading, error, refetch } = useQuery<GetAllDriversResponse>(
    GET_ALL_DRIVERS,
    {
      variables: {
        email: emailFilter || undefined,
        idNumber: idNumberFilter || undefined,
        idType: idTypeFilter || undefined,
        phoneNumber: phoneNumberFilter || undefined,
        registrationStatus: registrationStatusFilter || undefined,
        pagination: {
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const [updateDriverStatus, { loading: updateLoading }] = useMutation(
    UPDATE_DRIVER_STATUS,
    {
      onCompleted: () => {
        message.success(t("message.success.data_updated"));
        setIsModalVisible(false);
        form.resetFields();
        refetch();
      },
      onError: (error) => {
        message.error(error.message || t("message.error.data_update_failed"));
      },
    }
  );

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setEmailFilter(emailInput);
    setPhoneNumberFilter(phoneNumberInput);
    setIdNumberFilter(idNumberInput);
    setIdTypeFilter(idTypeInput);
    setRegistrationStatusFilter(registrationStatusInput);
  };

  const handleResetFilters = () => {
    setCurrentPage(1);
    setEmailInput("");
    setPhoneNumberInput("");
    setIdNumberInput("");
    setIdTypeInput(undefined);
    setRegistrationStatusInput(undefined);
    setEmailFilter("");
    setPhoneNumberFilter("");
    setIdNumberFilter("");
    setIdTypeFilter(undefined);
    setRegistrationStatusFilter(undefined);
  };

  const handleOpenStatusModal = (driver: { id: number; name: string }) => {
    setSelectedDriver(driver);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedDriver(null);
    form.resetFields();
  };

  const handleSubmitStatus = async (values: any) => {
    if (!selectedDriver) return;

    await updateDriverStatus({
      variables: {
        input: {
          userId: selectedDriver.id,
          status: values.status,
          rejectionReason: values.rejectionReason || undefined,
        },
      },
    });
  };

  const drivers: Driver[] = data?.getAllDrivers?.drivers ?? [];
  const totalDrivers = data?.getAllDrivers?.total ?? 0;

  const registrationStatuses = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...REGISTRATION_STATUS_OPTIONS,
            ...drivers
              .map((driver: Driver) => driver?.driverInfo?.registrationStatus)
              .filter(
                (status): status is DriverRegistrationStatus =>
                  status !== null && status !== undefined
              ),
          ]
        )
      ),
    [drivers]
  );

  const getStatusColor = (status?: string | null) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "success";
      case "PENDING":
        return "warning";
      case "UNDER_REVIEW":
        return "processing";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  // All filters are now handled server-side via GraphQL query variables
  const filteredDrivers = drivers;

  const dataSource =
    filteredDrivers.map((driver: Driver, index: number) => ({
      key:
        driver?.id ??
        driver?.driverInfo?.email ??
        driver?.phoneNumber ??
        driver?.name ??
        index,
      id: driver?.id ?? "-",
      name: driver?.name ?? "-",
      email: driver?.driverInfo?.email ?? "-",
      phoneNumber: driver?.phoneNumber ?? "-",
      registrationStatus: driver?.driverInfo?.registrationStatus ?? "-",
      idNumber: driver?.driverInfo?.idNumber ?? "-",
      idType: driver?.driverInfo?.idType ?? "-",
      rejectionReason: driver?.driverInfo?.rejectionReason ?? "-",
    })) ?? [];

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: t("label.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("label.email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("label.phone"),
      dataIndex: "phoneNumber",
      key: "phoneNumber",
    },
    {
      title: t("label.driver.id_number"),
      dataIndex: "idNumber",
      key: "idNumber",
    },
    {
      title: t("label.driver.id_type"),
      dataIndex: "idType",
      key: "idType",
      render: (idType: string) => {
        if (!idType || idType === "-") return "-";
        const matched = identityTypesMap[idType];
        if (matched) {
          const isArabic = i18n.language?.startsWith("ar");
          return isArabic ? matched.nameAr : matched.nameEn;
        }
        return t(`label.id_type.${idType.toLowerCase()}`, {
          defaultValue: idType,
        });
      },
    },
    {
      title: t("label.driver.rejection_reason"),
      dataIndex: "rejectionReason",
      key: "rejectionReason",
    },
    {
      title: t("label.driver.registration_status"),
      dataIndex: "registrationStatus",
      key: "registrationStatus",
      render: (status: string) => {
        if (!status || status === "-") return "-";
        return (
          <Tag color={getStatusColor(status)}>
            {t(`label.registration_status.${status.toLowerCase()}`, { defaultValue: status })}
          </Tag>
        );
      },
    },
    {
      title: t("label.actions"),
      key: "actions",
      width: 120,
      align: "center" as const,
      render: (_: any, record: any) => (
        <Tooltip title={t("button.view")}>
          <Button
            type="primary"
            shape="circle"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/drivers/${record.id}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <RobinPage
      title={t("label.menu.drivers")}
      actions={
        <Space size="middle">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            disabled={loading}
          >
            {t("button.refresh")}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Flex gap="middle" wrap="wrap" style={{ width: "100%" }}>
          <Input
            allowClear
            value={emailInput}
            placeholder={t("label.email")}
            onChange={(e) => setEmailInput(e.target.value)}
            onPressEnter={handleApplyFilters}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Input
            allowClear
            value={phoneNumberInput}
            placeholder={t("label.phone")}
            onChange={(e) => setPhoneNumberInput(e.target.value)}
            onPressEnter={handleApplyFilters}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Input
            allowClear
            value={idNumberInput}
            placeholder={t("label.driver.id_number")}
            onChange={(e) => setIdNumberInput(e.target.value)}
            onPressEnter={handleApplyFilters}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Select
            allowClear
            value={idTypeInput}
            placeholder={t("label.driver.id_type")}
            options={identityTypes.map((type) => ({
              value: type.id,
              label: i18n.language?.startsWith("ar") ? type.nameAr : type.nameEn,
            }))}
            onChange={(value) => setIdTypeInput(value)}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Select
            allowClear
            value={registrationStatusInput}
            placeholder={t("label.driver.registration_status")}
            options={registrationStatuses.map((status) => ({
              value: status,
              label: t(`driver.status.${status.toLowerCase()}`, { defaultValue: status }),
            }))}
            onChange={(value) => setRegistrationStatusInput(value)}
            style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}
          />
          <Space.Compact style={{ flex: "1 1 calc(33.333% - 8px)", minWidth: 200 }}>
            <Button
              type="primary"
              onClick={handleApplyFilters}
              style={{ flex: 1 }}
            >
              {t("button.search")}
            </Button>
            <Button
              onClick={handleResetFilters}
              style={{ flex: 1 }}
            >
              {t("button.reset_filters")}
            </Button>
          </Space.Compact>
        </Flex>
      </Space>
      {error ? (
        <Typography.Text type="danger">
          {t("error.failed_to_load")}
        </Typography.Text>
      ) : null}
      <Table
        loading={loading}
        dataSource={dataSource}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalDrivers,
          showSizeChanger: true,
          showTotal: (total) => t("label.total_items", { total }),
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      {/* Change Status Modal */}
      <Modal
        title={t("label.driver.change_status")}
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitStatus}
          initialValues={{ status: undefined, rejectionReason: "" }}
        >
          <Form.Item
            name="status"
            label={t("label.driver.registration_status")}
            rules={[
              { required: true, message: t("message.error.field_required") },
            ]}
          >
            <Select
              placeholder={t("label.driver.registration_status")}
              options={REGISTRATION_STATUS_OPTIONS.map((status) => ({
                value: status,
                label: t(`label.registration_status.${status.toLowerCase()}`, { defaultValue: status }),
              }))}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
          >
            {({ getFieldValue }) =>
              getFieldValue("status") === "REJECTED" ? (
                <Form.Item
                  name="rejectionReason"
                  label={t("label.driver.rejection_reason")}
                  rules={[
                    { required: true, message: t("message.error.field_required") },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder={t("label.driver.rejection_reason")}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseModal}>
                {t("button.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" loading={updateLoading}>
                {t("button.save")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </RobinPage>
  );
}

export default DriverList;

